import React, { useContext, useEffect } from 'react';
import parse from "html-react-parser";
import { useQuery } from "@apollo/client";
import { GET_BACKEND_CONFIG } from "../commonQueries";
import { useLocation } from "react-router-dom";
import readConfig from "../utils/readClientConfiguration";
import appConfiguration from '../config/default.json';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

// Utworzenie prostego kontekstu dla metadanych
export const MetaContext = React.createContext({
  title: "Emmezeta webshop - sigurna online trgovina",
  meta: [],
  links: [],
  scripts: []
});

export const useMetaContext = () => useContext(MetaContext);

// Funkcja do aktualizowania metadanych w DOM (tylko po stronie klienta)
const updateDocumentMeta = (meta) => {
  // We're not using this function anymore with react-helmet-async

  // Aktualizacja tytułu
  if (meta.title) {
    document.title = meta.title;
  }

  // Aktualizacja meta tagów
  meta.meta.forEach(({ name, property, content }) => {
    let selector = '';
    if (name) selector = `meta[name="${name}"]`;
    else if (property) selector = `meta[property="${property}"]`;
    
    if (!selector) return;

    let element = document.querySelector(selector);
    if (!element) {
      element = document.createElement('meta');
      if (name) element.setAttribute('name', name);
      if (property) element.setAttribute('property', property);
      document.head.appendChild(element);
    }
    
    element.setAttribute('content', content || '');
  });

  // Aktualizacja linków
  meta.links.forEach(({ rel, href }) => {
    let element = document.querySelector(`link[rel="${rel}"][href="${href}"]`);
    if (!element) {
      element = document.createElement('link');
      element.setAttribute('rel', rel);
      element.setAttribute('href', href);
      document.head.appendChild(element);
    }
  });

  // Aktualizacja skryptów
  meta.scripts.forEach((scriptData) => {
    const { src, type, defer, async: scriptAsync, innerHTML } = scriptData;
    
    // Skip if already exists
    if (src && document.querySelector(`script[src="${src}"]`)) {
      return;
    }
    
    const script = document.createElement('script');
    if (type) script.type = type;
    if (defer) script.defer = true;
    if (scriptAsync) script.async = true;
    if (src) script.src = src;
    if (innerHTML) script.innerHTML = innerHTML;
    
    document.head.appendChild(script);
  });
};

const HeadMetaTags = () => {
    const metaContext = useMetaContext();
    const { i18n } = useTranslation();
    
    const { baseUrl } = appConfiguration.config || { baseUrl: '' };
    const { data: backendConfig, loading: backendConfigLoading, error: backendConfigError } = useQuery(GET_BACKEND_CONFIG);
    const location = useLocation();
    const currentUrl = baseUrl + location.pathname;
    const htmlLang = i18n.language || appConfiguration.i18n?.lng || 'hr';
    
    // Używamy Helmet do zarządzania metadanymi
    if (backendConfigLoading || backendConfigError || !backendConfig || !backendConfig.configProvider) {
        // Podstawowe metadane, gdy dane jeszcze nie są dostępne
        return (
            <Helmet htmlAttributes={{ lang: htmlLang }}>
                <title>Emmezeta webshop - sigurna online trgovina</title>
                <meta name="viewport" content="width=device-width,initial-scale=1" />
                <meta name="description" content="Emmezeta webshop - vaša sigurna online trgovina" />
                <link rel="canonical" href={currentUrl} />
                <link rel="icon" href="https://s3.emmezeta.hr/falcon/favicon.ico" />
                <link rel="manifest" href="/manifest.json" />
            </Helmet>
        );
    }

    try {
        const { default_meta_description, head_scripts, samito } = backendConfig.configProvider;
        const pageTitle = metaContext.title || "Emmezeta webshop - sigurna online trgovina";
        
        return (
            <Helmet htmlAttributes={{ lang: htmlLang }}>
                {/* Podstawowe metadane */}
                <title>{pageTitle}</title>
                <meta name="viewport" content="width=device-width,initial-scale=1" />
                <meta name="description" content={default_meta_description || ''} />
                <meta name="keywords" content="" />
                <meta name="theme-color" content="#fff" />
                <meta name="format-detection" content="telephone=yes" />
                
                {/* Open Graph metadane */}
                <meta property="og:title" content={pageTitle} />
                <meta property="og:type" content="website" />
                <meta property="og:description" content={default_meta_description || ''} />
                <meta property="og:url" content={currentUrl} />
                <meta property="og:image" content="https://s3.emmezeta.hr/falcon/emmezeta-placeholder.png" />
                <meta property="og:image:height" content="240" />
                <meta property="og:image:width" content="240" />
                
                {/* Link kanoniczny */}
                <link rel="canonical" href={currentUrl} />
                
                {/* Favicon */}
                <link rel="icon" href="https://s3.emmezeta.hr/falcon/favicon.ico" />
                
                {/* PWA Manifest */}
                <link rel="manifest" href="/manifest.json" />
                
                {/* Dodawanie skryptów Samito */}
                {samito && parseInt(samito.samito_enable) === 1 && (
                    <>
                        {(() => {
                            try {
                                const { sid: samito_sid, url: samito_url } = readConfig(appConfiguration, 'samito');
                                const encodedUrl = encodeURIComponent(baseUrl + location.pathname);
                                return (
                                    <>
                                        <script src={`${samito_url}/samito.js`} type="text/javascript" defer async />
                                        <script type="text/javascript">{`var SavecartNS={sid: '${samito_sid}',url: '${encodedUrl}'};`}</script>
                                    </>
                                );
                            } catch (error) {
                                console.error("Błąd konfiguracji samito:", error);
                                return null;
                            }
                        })()}
                    </>
                )}
                
                {/* Dodawanie skryptów z head_scripts */}
                {head_scripts && typeof head_scripts === 'string' && head_scripts.trim() !== '' && (
                    <script type="text/javascript">{`
                        document.addEventListener('DOMContentLoaded', function() {
                            const headScripts = ${JSON.stringify(head_scripts)};
                            const tempDiv = document.createElement('div');
                            tempDiv.innerHTML = headScripts;
                            
                            const scripts = tempDiv.querySelectorAll('script');
                            scripts.forEach(script => {
                                try {
                                    const newScript = document.createElement('script');
                                    Array.from(script.attributes).forEach(attr => {
                                        newScript.setAttribute(attr.name, attr.value);
                                    });
                                    newScript.innerHTML = script.innerHTML;
                                    document.head.appendChild(newScript);
                                } catch (error) {
                                    console.error('Error adding script from head_scripts:', error);
                                }
                            });
                        });
                    `}</script>
                )}
            </Helmet>
        );
    } catch (error) {
        console.error('Error rendering helmet meta tags:', error);
        return (
            <Helmet htmlAttributes={{ lang: htmlLang }}>
                <title>Emmezeta webshop</title>
                <meta name="viewport" content="width=device-width,initial-scale=1" />
                <meta name="description" content="Emmezeta webshop - vaša sigurna online trgovina" />
                <link rel="icon" href="https://s3.emmezeta.hr/falcon/favicon.ico" />
                <link rel="manifest" href="/manifest.json" />
            </Helmet>
        );
    }
};

export default HeadMetaTags;